/**
 * @see https://umijs.org/docs/max/access#access
 * 说明：带 Section 结尾的 access 用于父级菜单，避免子路由有权限但侧栏不展开的问题。
 * */
import { isProdBEnv } from '@/utils/isProdBEnv';

export default function access(
  initialState: { currentUser?: { permissions?: string[]; belongType?: string } } | undefined,
) {
  const permissions = initialState?.currentUser?.permissions || [];
  const belongType = String(initialState?.currentUser?.belongType || 'PLATFORM').toUpperCase();
  const hasPerm = (code: string) => permissions.includes(code);
  const hasAny = (codes: string[]) => codes.some((c) => hasPerm(c));

  const isPlatform = belongType === 'PLATFORM';

  /** 与 config/routes.ts 中 ENABLE_FINANCE_PHASE3 同步；任一对账/结算原子权限即可见侧栏分组 */
  const canFinancePhase3 = hasAny([
    'edu.finance.settlement',
    'edu.bill.trade.view',
    'edu.bill.statement.view',
    'edu.bill.withdraw.view',
    'edu.settlement.summary.view',
    'edu.settlement.detail.view',
    'edu.compensation.view',
    'edu.compensation.confirm',
  ]);

  /** 金融数据看板：侧栏关闭（路由保留便于以后打开） */
  const canLargeMarketBoard = false;
  const canLargeMarketBill = isPlatform && hasPerm('edu.market.bill.view');
  const canLargeMarketSection = canLargeMarketBill;

  const canVintageView = isPlatform && hasAny(['edu.vintage.view', 'edu.market.view']);
  const canVintageExport = isPlatform && hasAny(['edu.vintage.export', 'edu.vintage.view']);
  const canVintageCompRuleEdit = isPlatform && hasPerm('edu.vintage.comp_rule.edit');
  const canVintageRebuild = isPlatform && hasPerm('edu.vintage.rebuild');
  /** 贷后报表：仅医美后台；独立权限点 edu.vintage.post_loan.view */
  const canPostLoanReport = isProdBEnv() && isPlatform && hasPerm('edu.vintage.post_loan.view');
  const canVintageSection = canVintageView || canPostLoanReport;

  /** 方案/费率仅平台维护；edu.merchant.scheme.manage 只用于平台账号 */
  const canPlatformProductScheme = isPlatform && hasPerm('edu.merchant.scheme.manage');

  const canMerchantManage = hasAny([
    'edu.merchant.manage',
    'edu.merchant.application.view',
  ]);
  const canCourseManage = hasPerm('edu.course.manage');
  const canMerchantSection = canMerchantManage || canCourseManage;

  const canRiskOrderAudit = hasPerm('edu.order.risk.audit');
  const canRiskMerchantAuditList = hasAny([
    'edu.risk.merchant.list.view',
    'edu.merchant.application.audit',
  ]);
  const canRiskMerchantChecklist = hasAny([
    'edu.risk.merchant.checklist.view',
    'edu.merchant.application.audit',
  ]);
  const canRiskManage = canRiskOrderAudit;
  const canRiskSection =
    canRiskOrderAudit || canRiskMerchantAuditList || canRiskMerchantChecklist;

  const canRefundManage = hasPerm('edu.refund.manage');
  const canRefundSection = canRefundManage;

  const orderPageLegacy = ['edu.order.view', 'edu.merchant.manage'] as const;
  const canOrderOrderList = hasAny(['edu.order.page.order_list', ...orderPageLegacy]);
  const canOrderLendingList = hasAny(['edu.order.page.lending_list', ...orderPageLegacy]);
  const canOrderRepaymentDetail = hasAny(['edu.order.page.repayment_detail', ...orderPageLegacy]);
  const canOrderDeductionList = hasAny(['edu.order.page.deduction_list', ...orderPageLegacy]);
  const canOrderOverdueDetail = hasAny(['edu.order.page.overdue_detail', ...orderPageLegacy]);

  const canOrderView = hasAny([
    'edu.order.view',
    'edu.merchant.manage',
    'edu.order.page.order_list',
    'edu.order.page.lending_list',
    'edu.order.page.repayment_detail',
    'edu.order.page.deduction_list',
    'edu.order.page.overdue_detail',
  ]);

  const canCustomerView = hasAny([
    'edu.customer.list.view',
    'edu.order.view',
    'edu.merchant.manage',
  ]);
  const canCustomerSection = canCustomerView;

  const rbacAccountLegacy = [
    'rbac.account.view',
    'rbac.account.create',
    'rbac.account.edit',
    'rbac.account.toggle',
    'rbac.account.reset_password',
  ] as const;
  const rbacPlatformAccount = [
    'rbac.account.platform.view',
    'rbac.account.platform.create',
    'rbac.account.platform.edit',
    'rbac.account.platform.toggle',
    'rbac.account.platform.reset_password',
  ] as const;
  const rbacMerchantAccount = [
    'rbac.account.merchant.view',
    'rbac.account.merchant.create',
    'rbac.account.merchant.edit',
    'rbac.account.merchant.toggle',
    'rbac.account.merchant.reset_password',
  ] as const;

  const canUserManageSection = hasAny([
    ...rbacAccountLegacy,
    ...rbacPlatformAccount,
    ...rbacMerchantAccount,
    'rbac.role.view',
    'rbac.user.assign_role',
    'rbac.role.assign_permission',
    'rbac.permission.view',
  ]);

  /** 侧栏「平台账号」：仅平台身份，且能查看平台账号列表 */
  const canPlatformAccounts =
    isPlatform && hasAny(['rbac.account.view', 'rbac.account.platform.view']);
  /** 侧栏「商户账号」 */
  const canMerchantAccounts = hasAny(['rbac.account.view', 'rbac.account.merchant.view']);

  const canPlatformAccountCreate = isPlatform && hasAny(['rbac.account.create', 'rbac.account.platform.create']);
  const canMerchantAccountCreate = hasAny(['rbac.account.create', 'rbac.account.merchant.create']);
  const canPlatformAccountEdit = isPlatform && hasAny(['rbac.account.edit', 'rbac.account.platform.edit']);
  const canMerchantAccountEdit = hasAny(['rbac.account.edit', 'rbac.account.merchant.edit']);
  const canPlatformAccountToggle = isPlatform && hasAny(['rbac.account.toggle', 'rbac.account.platform.toggle']);
  const canMerchantAccountToggle = hasAny(['rbac.account.toggle', 'rbac.account.merchant.toggle']);
  const canPlatformAccountResetPwd = isPlatform && hasAny(['rbac.account.reset_password', 'rbac.account.platform.reset_password']);
  const canMerchantAccountResetPwd = hasAny(['rbac.account.reset_password', 'rbac.account.merchant.reset_password']);

  /** 新氧导流 · 资方管理 / 线下认款（医美 prod-b） */
  const canFunderView = isProdBEnv() && isPlatform && hasPerm('edu.funder.view');
  const canFunderEdit = isProdBEnv() && isPlatform && hasPerm('edu.funder.edit');
  const canFunderSection = canFunderView || canFunderEdit;
  const canOfflineRepayView =
    isProdBEnv() &&
    isPlatform &&
    hasAny(['edu.offline_repay.view', 'edu.offline_repay.approve']);
  const canOfflineRepayApply =
    isProdBEnv() &&
    isPlatform &&
    hasAny(['edu.offline_repay.apply', 'edu.order.page.order_list', 'edu.order.view']);
  const canOfflineRepayApprove =
    isProdBEnv() && isPlatform && hasPerm('edu.offline_repay.approve');

  const canOrderSection = canOrderView || canOfflineRepayView;

  const canAgreementView = hasPerm('edu.system.agreement.view');
  /** 订单标签菜单：侧栏关闭（路由保留便于以后打开） */
  const canOrderTagView = false;
  /** 未单独授 tag.manage 时：平台账号且有订单列表/导出权，默认可维护标签（运营自用） */
  const canOrderTagManage =
    hasPerm('edu.order.tag.manage') ||
    (isPlatform &&
      hasAny([
        'edu.order.export',
        'edu.order.page.order_list',
        'edu.order.view',
        'edu.merchant.manage',
      ]));
  const canFadadaConfig = isPlatform && hasPerm('edu.merchant.manage');
  const canSystemSection = canAgreementView || canFadadaConfig;

  return {
    canFinancePhase3,

    canLargeMarketBoard,
    canLargeMarketBill,
    canLargeMarketSection,
    /** 兼容旧引用（页面级兜底） */
    canLargeMarketView: canLargeMarketSection,

    canVintageSection,
    canVintageView,
    canVintageExport,
    canVintageCompRuleEdit,
    canVintageRebuild,
    canPostLoanReport,

    /** 一期门店菜单整组关闭（路由保留便于以后打开） */
    canStoreSection: false,
    canStoreManage: hasPerm('edu.store.manage'),

    canMerchantSection,
    canMerchantManage,
    canPlatformProductScheme,
    canCourseManage,

    canRiskSection,
    canRiskManage,
    canRiskOrderAudit,
    canRiskMerchantAuditList,
    canRiskMerchantChecklist,

    canRefundSection,
    canRefundManage,

    canOrderSection,
    canOrderView,
    canOrderOrderList,
    canOrderLendingList,
    canOrderRepaymentDetail,
    canOrderDeductionList,
    canOrderOverdueDetail,
    canOrderExport: hasAny(['edu.order.export', 'edu.order.tag.export']),
    canOrderTagView,
    canOrderTagManage,
    canOrderTagExport: hasAny(['edu.order.tag.export', 'edu.order.export']),
    canOfflineRepayView,
    canOfflineRepayApply,
    canOfflineRepayApprove,

    canFunderSection,
    canFunderView,
    canFunderEdit,

    canCustomerSection,
    canCustomerView,

    canUserManageSection,
    canPlatformAccounts,
    canMerchantAccounts,
    canPlatformAccountCreate,
    canMerchantAccountCreate,
    canPlatformAccountEdit,
    canMerchantAccountEdit,
    canPlatformAccountToggle,
    canMerchantAccountToggle,
    canPlatformAccountResetPwd,
    canMerchantAccountResetPwd,

    canAgreementView,
    canFadadaConfig,
    canSystemSection,

    /** 角色/权限等子页：仍用原子权限 */
    canAdmin: hasAny([
      'rbac.account.view',
      'rbac.account.platform.view',
      'rbac.account.merchant.view',
      'rbac.role.view',
      'rbac.user.assign_role',
    ]),
    canFinanceManage: hasPerm('edu.finance.settlement'),
    /** 兼容：任意一侧有新增/编辑等权即为 true（列表页请用 platform/merchant 细分） */
    canAccountCreate: hasAny([
      'rbac.account.create',
      'rbac.account.platform.create',
      'rbac.account.merchant.create',
    ]),
    canAccountEdit: hasAny(['rbac.account.edit', 'rbac.account.platform.edit', 'rbac.account.merchant.edit']),
    canAccountToggle: hasAny(['rbac.account.toggle', 'rbac.account.platform.toggle', 'rbac.account.merchant.toggle']),
    canAccountResetPwd: hasAny([
      'rbac.account.reset_password',
      'rbac.account.platform.reset_password',
      'rbac.account.merchant.reset_password',
    ]),
    canRoleCreate: hasAny(['rbac.role.create']),
    canRoleEdit: hasAny(['rbac.role.edit']),
    canRoleDelete: hasAny(['rbac.role.delete']),
    canRoleAssignPerm: hasAny(['rbac.role.assign_permission']),
    canUserAssignRole: hasAny(['rbac.user.assign_role']),
    canPermissionView: hasAny(['rbac.permission.view', 'rbac.role.view']),
    canPermissionCreate: hasAny(['rbac.permission.create', 'rbac.role.assign_permission']),
    canPermissionEdit: hasAny(['rbac.permission.edit', 'rbac.role.assign_permission']),
    canPermissionToggle: hasAny(['rbac.permission.toggle', 'rbac.role.assign_permission']),
    canMerchantAppCreate: hasAny(['edu.merchant.application.create', 'edu.merchant.manage']),
    canMerchantAppSubmit: hasAny(['edu.merchant.application.submit', 'edu.merchant.manage']),
    /** 仅平台审核岗；不再用 merchant.manage 兜底，避免商户侧误显审核入口 */
    canMerchantAppAudit: hasAny(['edu.merchant.application.audit']),
    canRefundInitiate: hasAny(['edu.refund.initiate', 'edu.refund.manage']),
    canRefundConfirm: hasAny(['edu.refund.confirm', 'edu.refund.manage']),
    canBillTradeView: hasAny(['edu.bill.trade.view', 'edu.finance.settlement']),
    canBillTradeExport: hasAny(['edu.bill.trade.export', 'edu.finance.settlement']),
    canBillStatementView: hasAny(['edu.bill.statement.view', 'edu.finance.settlement']),
    canBillStatementOpsConfirm: hasAny(['edu.bill.statement.ops_confirm', 'edu.finance.settlement']),
    canBillStatementFinanceConfirm: hasAny(['edu.bill.statement.finance_confirm', 'edu.finance.settlement']),
    canBillStatementExport: hasAny(['edu.bill.statement.export', 'edu.finance.settlement']),
    canBillWithdrawView: hasAny(['edu.bill.withdraw.view', 'edu.finance.settlement']),
    canBillWithdrawApply: hasAny(['edu.bill.withdraw.apply', 'edu.finance.settlement']),
    canSettlementSummaryView: hasAny(['edu.settlement.summary.view', 'edu.finance.settlement']),
    canSettlementSummaryPay: hasAny(['edu.settlement.summary.pay', 'edu.finance.settlement']),
    canSettlementSummaryExport: hasAny(['edu.settlement.summary.export', 'edu.finance.settlement']),
    canSettlementDetailView: hasAny(['edu.settlement.detail.view', 'edu.finance.settlement']),
    canSettlementDetailExport: hasAny(['edu.settlement.detail.export', 'edu.finance.settlement']),
    canCompensationView: hasAny(['edu.compensation.view', 'edu.finance.settlement']),
    canCompensationConfirm: hasAny(['edu.compensation.confirm', 'edu.finance.settlement']),
  };
}
